package app.footyguide.web.data

import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.GameRepository
import app.footyguide.domain.model.MatchStatus

/**
 * Repositório de Classificação e Tabela do Brasileirão e outras ligas.
 * Calcula a classificação consolidada a partir dos jogos reais da temporada no Firestore.
 */
object WebStandingsRepository {

    private data class MutableTeamStats(
        val teamId: String,
        val name: String,
        val initials: String,
        val logoUrl: String,
        var points: Int = 0,
        var played: Int = 0,
        var wins: Int = 0,
        var draws: Int = 0,
        var losses: Int = 0,
        var goalsFor: Int = 0,
        var goalsAgainst: Int = 0
    )

    fun computeStandingsFromGames(games: List<Game>): List<StandingRow> {
        val statsMap = linkedMapOf<String, MutableTeamStats>()
        ClubCatalog.CLUBS.forEach { club ->
            statsMap[club.slug] = MutableTeamStats(
                teamId = club.slug,
                name = club.name,
                initials = club.initials,
                logoUrl = club.logoUrl
            )
        }

        val validClubs = statsMap.keys.toSet()
        // In a double round-robin league (Brasileirão), each (homeId, awayId) pair plays once.
        // Deduplicating by homeId_vs_awayId (keeping the latest kickoff) eliminates duplicate
        // documents created when a match shifted UTC/BRT date or was re-ingested.
        val canonicalFinishedGames = games
            .filter { it.matchStatus == MatchStatus.FINISHED && it.homeScore != null && it.awayScore != null }
            .sortedBy { it.kickoff }
            .associateBy { game ->
                val homeId = ClubCatalog.bySlug(game.homeTeamId)?.slug ?: game.homeTeamId
                val awayId = ClubCatalog.bySlug(game.awayTeamId)?.slug ?: game.awayTeamId
                "${homeId}_vs_${awayId}"
            }
            .values

        canonicalFinishedGames.forEach { game ->
            val hScore = game.homeScore ?: return@forEach
            val aScore = game.awayScore ?: return@forEach
            val homeClub = ClubCatalog.bySlug(game.homeTeamId)
            val awayClub = ClubCatalog.bySlug(game.awayTeamId)
            val homeId = homeClub?.slug ?: game.homeTeamId
            val awayId = awayClub?.slug ?: game.awayTeamId
            if (homeId == awayId || homeId !in validClubs || awayId !in validClubs) return@forEach

            val homeStats = statsMap.getOrPut(homeId) {
                MutableTeamStats(
                    teamId = homeId,
                    name = homeClub?.name ?: homeId.replaceFirstChar { it.uppercase() },
                    initials = homeClub?.initials ?: homeId.take(3).uppercase(),
                    logoUrl = homeClub?.logoUrl ?: ""
                )
            }
            val awayStats = statsMap.getOrPut(awayId) {
                MutableTeamStats(
                    teamId = awayId,
                    name = awayClub?.name ?: awayId.replaceFirstChar { it.uppercase() },
                    initials = awayClub?.initials ?: awayId.take(3).uppercase(),
                    logoUrl = awayClub?.logoUrl ?: ""
                )
            }

            homeStats.played += 1
            awayStats.played += 1
            homeStats.goalsFor += hScore
            homeStats.goalsAgainst += aScore
            awayStats.goalsFor += aScore
            awayStats.goalsAgainst += hScore

            when {
                hScore > aScore -> {
                    homeStats.wins += 1
                    homeStats.points += 3
                    awayStats.losses += 1
                }
                aScore > hScore -> {
                    awayStats.wins += 1
                    awayStats.points += 3
                    homeStats.losses += 1
                }
                else -> {
                    homeStats.draws += 1
                    awayStats.draws += 1
                    homeStats.points += 1
                    awayStats.points += 1
                }
            }
        }

        val sorted = statsMap.values.sortedWith(
            compareByDescending<MutableTeamStats> { it.points }
                .thenByDescending { it.wins }
                .thenByDescending { it.goalsFor - it.goalsAgainst }
                .thenByDescending { it.goalsFor }
                .thenBy { it.name }
        )

        return sorted.mapIndexed { index, s ->
            val pos = index + 1
            val zone = when {
                pos in 1..4 -> StandingZone.LIBERTADORES
                pos in 5..6 -> StandingZone.PRE_LIBERTADORES
                pos in 7..12 -> StandingZone.SUL_AMERICANA
                pos in 17..20 -> StandingZone.RELEGATION
                else -> StandingZone.NEUTRAL
            }
            StandingRow(
                position = pos,
                teamId = s.teamId,
                name = s.name,
                initials = s.initials,
                logoUrl = s.logoUrl,
                points = s.points,
                played = s.played,
                wins = s.wins,
                draws = s.draws,
                losses = s.losses,
                goalDifference = s.goalsFor - s.goalsAgainst,
                zone = zone
            )
        }
    }

    private val memCache = mutableMapOf<String, List<StandingRow>>()

    fun clearCache() {
        memCache.clear()
    }

    suspend fun getStandings(
        competitionId: String = "brasileirao",
        gameRepository: GameRepository = WebGameRepository()
    ): List<StandingRow> {
        memCache[competitionId]?.let { return it }
        if (competitionId == "brasileirao") {
            return try {
                val games = gameRepository.getGamesByCompetition("brasileirao", "2026-01-01", "2026-12-31")
                if (games.isNotEmpty()) {
                    val computed = computeStandingsFromGames(games)
                    memCache[competitionId] = computed
                    computed
                } else {
                    emptyList()
                }
            } catch (e: Exception) {
                console.warn("[WebStandingsRepository] Falha ao calcular tabela do Brasileirão: ${e.message}")
                emptyList()
            }
        }
        return emptyList()
    }
}
